import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import {
  BULK_MAX_ROWS,
  fitToReference,
  placementAreaFromMm,
  placementMmToRelative,
} from '@designdeck/shared';
import {
  Button,
  Input,
  StatusPill,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui';
import { BULK_TITLE_MAX_LENGTH } from '@/lib/bulk/derive-title';
import {
  isRowTitleMissing,
  rowProductCount,
  type BulkCountProduct,
} from '@/lib/bulk/pairing';
import {
  useBulkCreationStore,
  type BulkCell,
  type BulkReferencePlacements,
  type BulkRow,
  type BulkZone,
} from '@/stores/bulkCreationStore';
import { MotifCell } from './MotifCell';

const SIDES: readonly BulkZone[] = ['front', 'back'];

function readySize(
  cell: BulkCell | undefined
): { width: number; height: number } | null {
  if (cell?.state !== 'ready' || !cell.width || !cell.height) return null;
  return { width: cell.width, height: cell.height };
}

/**
 * Whether a ready motif of the row would be rendered smaller than on the
 * reference (R22). Uses the same `fitToReference` rule as the bulk job, with
 * the reference's stored placement of that side; an ignored back never
 * counts.
 */
export function rowWillShrink(
  row: BulkRow,
  placements: BulkReferencePlacements | undefined,
  backEnabled: boolean
): boolean {
  return SIDES.some((side) => {
    if (side === 'back' && !backEnabled) return false;
    const size = readySize(row[side]);
    const reference = placements?.[side];
    if (!size || !reference) return false;
    // The rule is relative and unit-free: fed the reference's millimetres.
    return fitToReference(
      placementMmToRelative(reference.placementMm, reference.area),
      size.height / size.width,
      placementAreaFromMm(reference.area)
    ).shrunk;
  });
}

export interface MotifTableProps {
  rows: readonly BulkRow[];
  activeProducts: readonly BulkCountProduct[];
  backEnabled: boolean;
  placements: BulkReferencePlacements | undefined;
  /** Local files of uploading cells, for the thumbnail preview. */
  localFiles: ReadonlyMap<string, File>;
}

/**
 * The row table of step 02 (R19–R23): number, front, back, working title,
 * product count and row delete. Scrolls horizontally on narrow screens (the
 * kit table wraps itself in an overflow container).
 */
export function MotifTable({
  rows,
  activeProducts,
  backEnabled,
  placements,
  localFiles,
}: MotifTableProps) {
  const { t } = useTranslation();
  const removeCell = useBulkCreationStore((state) => state.removeCell);
  const removeRow = useBulkCreationStore((state) => state.removeRow);
  const moveBack = useBulkCreationStore((state) => state.moveBack);
  const setTitle = useBulkCreationStore((state) => state.setTitle);

  return (
    <TooltipProvider delayDuration={300}>
      <Table data-testid="bulk-motif-table" className="min-w-[56rem]">
        <TableHead>
          <tr>
            <TableHeaderCell className="w-12">
              {t('seller.bulk.motifs.table.number')}
            </TableHeaderCell>
            <TableHeaderCell>
              {t('seller.bulk.motifs.table.front')}
            </TableHeaderCell>
            <TableHeaderCell>
              {t('seller.bulk.motifs.table.back')}
            </TableHeaderCell>
            <TableHeaderCell className="min-w-[14rem]">
              {t('seller.bulk.motifs.table.title')}
            </TableHeaderCell>
            <TableHeaderCell className="text-right">
              {t('seller.bulk.motifs.table.products')}
            </TableHeaderCell>
            <TableHeaderCell className="w-12">
              <span className="sr-only">
                {t('seller.bulk.motifs.table.actions')}
              </span>
            </TableHeaderCell>
          </tr>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => {
            const number = index + 1;
            const isLast = index === rows.length - 1;
            const willShrink = rowWillShrink(row, placements, backEnabled);
            const titleMissing = isRowTitleMissing(row, backEnabled);
            const titleErrorId = `bulk-row-${row.id}-title-error`;
            return (
              <TableRow key={row.id} data-testid={`bulk-row-${number}`}>
                <TableCell className="font-mono text-mute">{number}</TableCell>
                <TableCell>
                  {row.front && (
                    <MotifCell
                      cell={row.front}
                      rowNumber={number}
                      localFile={localFiles.get(row.front.id)}
                      onRemove={() => removeCell(row.id, 'front')}
                    />
                  )}
                </TableCell>
                <TableCell>
                  {row.back && (
                    <MotifCell
                      cell={row.back}
                      rowNumber={number}
                      localFile={localFiles.get(row.back.id)}
                      ignored={!backEnabled}
                      onRemove={() => removeCell(row.id, 'back')}
                      moveControls={{
                        canMoveUp: index > 0,
                        // Down on the last row opens a back-only row; a
                        // back-only last row has nowhere to go, and the cap
                        // forbids a new row.
                        canMoveDown:
                          !isLast ||
                          (row.front !== undefined &&
                            rows.length < BULK_MAX_ROWS),
                        onMoveUp: () => moveBack(row.id, 'up'),
                        onMoveDown: () => moveBack(row.id, 'down'),
                      }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex flex-col items-start gap-1.5">
                    <Input
                      value={row.title}
                      maxLength={BULK_TITLE_MAX_LENGTH}
                      onChange={(event) => setTitle(row.id, event.target.value)}
                      aria-label={t('seller.bulk.motifs.table.titleLabel', {
                        number,
                      })}
                      error={titleMissing}
                      aria-describedby={titleMissing ? titleErrorId : undefined}
                    />
                    {titleMissing && (
                      <p id={titleErrorId} className="text-xs text-danger">
                        {t('seller.bulk.motifs.table.titleRequired')}
                      </p>
                    )}
                    {willShrink && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <StatusPill
                            variant="warning"
                            tabIndex={0}
                            data-testid={`bulk-row-${number}-shrink`}
                          >
                            {t('seller.bulk.motifs.willShrink')}
                          </StatusPill>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          {t('seller.bulk.motifs.willShrinkHint')}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </TableCell>
                <TableCell
                  className="text-right font-mono"
                  data-testid={`bulk-row-${number}-products`}
                >
                  {rowProductCount(row, activeProducts)}
                </TableCell>
                <TableCell>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => removeRow(row.id)}
                    aria-label={t('seller.bulk.motifs.removeRow', { number })}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TooltipProvider>
  );
}
